/**
 * RefundRequestDetailPage — admin review + Approve/Reject at
 * /refund-requests/:id (LEVEL_12_REFUND_AND_CANCELLATION.md §7/§17).
 *
 * Duplicate-click safety (§17.2): once a decision is in flight, or once the
 * request is no longer PENDING_REVIEW, both action buttons are removed
 * entirely — never left clickable-but-erroring. The backend is idempotent
 * regardless (an approve re-click is a safe no-op, never a second charge),
 * but the UI should never invite a second attempt in the first place.
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, XCircle } from 'lucide-react';
import {
  approveRefundRequest,
  closeRefundRequest,
  getRefundRequest,
  rejectRefundRequest,
  type AdminRefundRequest,
} from '@/src/infrastructure/lib/api';
import { ConfirmModal } from '../users/components/ConfirmModal';

function formatPaise(paise: number | null) {
  if (paise === null) return 'Full remaining amount at approval time';
  return `₹${(paise / 100).toFixed(2)}`;
}

function formatDateTime(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function RefundRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [row, setRow] = useState<AdminRefundRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionPending, setActionPending] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [confirmedCategory, setConfirmedCategory] = useState<'SERVICE_FAULT' | 'PAYMENT_ISSUE' | 'PACK_REFUND'>('SERVICE_FAULT');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [decisionReason, setDecisionReason] = useState('');
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeReason, setCloseReason] = useState('');

  function load() {
    if (!id) return;
    setLoading(true);
    setError(null);
    getRefundRequest(id)
      .then(setRow)
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load refund request'))
      .finally(() => setLoading(false));
  }

  useEffect(load, [id]);

  async function onApprove() {
    if (!id) return;
    setActionPending(true);
    setError(null);
    try {
      const updated = await approveRefundRequest(id, confirmedCategory);
      setRow(updated);
      setShowApproveConfirm(false);
    } catch (err) {
      const code = err instanceof Error ? (err as Error & { code?: string }).code : undefined;
      if (code === 'REFUND_EXECUTION_SURFACE_DISABLED') {
        setError(
          'Refund execution is disabled in this environment (BILLING_ENABLED is not set) — approving would move money with no way to confirm it. Contact ops before retrying.',
        );
      } else {
        setError(err instanceof Error ? err.message : 'Approval failed — the request is still PENDING_REVIEW');
      }
    } finally {
      setActionPending(false);
    }
  }

  async function onCloseRequest() {
    if (!id || closeReason.trim().length < 3) return;
    setActionPending(true);
    setError(null);
    try {
      const updated = await closeRefundRequest(id, closeReason.trim());
      setRow(updated);
      setShowCloseModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Closing request failed');
    } finally {
      setActionPending(false);
    }
  }

  async function onReject() {
    if (!id || decisionReason.trim().length < 3) return;
    setActionPending(true);
    setError(null);
    try {
      const updated = await rejectRefundRequest(id, decisionReason.trim());
      setRow(updated);
      setShowRejectModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rejection failed');
    } finally {
      setActionPending(false);
    }
  }

  if (loading) return <div className="p-8 text-on-surface-variant text-sm">Loading…</div>;
  if (error && !row) {
    return (
      <div className="p-8 space-y-4">
        <button onClick={() => navigate('/refund-requests')} className="text-sm text-primary inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to queue
        </button>
        <div className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-4 text-sm text-rose-700">{error}</div>
      </div>
    );
  }
  if (!row) return null;

  const isPending = row.status === 'PENDING_REVIEW';

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-3xl">
      <Link to="/refund-requests" className="text-sm text-primary inline-flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Back to queue
      </Link>

      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-display font-bold text-on-surface">Refund request #{row.id.slice(0, 8)}</h1>
        <p className="text-sm text-on-surface-variant">
          Status: {row.status.replace('_', ' ')}
          {row.status === 'APPROVED' && row.refundStatus && (
            <span className={row.refundStatus === 'FAILED' ? 'text-rose-600 font-semibold' : ''}>
              {' '}
              — refund {row.refundStatus.toLowerCase()}
            </span>
          )}
        </p>
      </header>

      <div className="rounded-2xl border border-border-subtle bg-surface-low/40 p-5 space-y-4">
        <Field label="Requested by (user id)" value={row.requestedByUserId} />
        <Field
          label="Purchase / Order"
          value={
            row.orderNumber ? (
              <span>
                <Link to={`/orders/${row.purchaseId}`} className="underline text-primary font-mono mr-2">
                  {row.orderNumber}
                </Link>
                <span className="text-xs text-on-surface-variant font-mono">({row.purchaseId})</span>
              </span>
            ) : (
              row.purchaseId
            )
          }
        />
        <Field label="Payment" value={row.paymentId} />
        <Field label="Amount requested" value={formatPaise(row.requestedAmountPaise)} />
        <Field label="Type" value={row.category === 'PAYMENT_ISSUE' ? 'Payment issue (duplicate / unauthorized / failed)' : 'Service fault (template / image / publish) — verify the invitation before approving'} />
        <Field label="Reason" value={row.reason} />
        <Field label="Source" value={row.source} />
        <Field label="Requested at" value={formatDateTime(row.createdAt)} />
        {row.reviewedAt && (
          <>
            <Field label="Reviewed by (admin id)" value={row.reviewedById ?? '—'} />
            <Field label="Reviewed at" value={formatDateTime(row.reviewedAt)} />
          </>
        )}
        {row.decisionReason && <Field label="Decision reason" value={row.decisionReason} />}
        {row.refundId && (
          <Field
            label="Linked refund (execution record)"
            value={`${row.refundId} — ${row.refundStatus ?? 'status unknown'}`}
            hint={
              row.refundStatus === 'FAILED'
                ? 'This refund failed at the gateway — no money moved. The user can submit a new request.'
                : 'Track the live status in Payments → Refund history for this payment.'
            }
          />
        )}
      </div>

      {error && row && (
        <div className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-3 text-sm text-rose-700">{error}</div>
      )}

      {isPending && (
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => {
              setConfirmedCategory(row.category ?? 'SERVICE_FAULT');
              setShowApproveConfirm(true);
            }}
            disabled={actionPending}
            className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90 disabled:opacity-50"
          >
            <CheckCircle2 className="w-4 h-4" /> Approve
          </button>
          <button
            type="button"
            onClick={() => setShowRejectModal(true)}
            disabled={actionPending}
            className="inline-flex items-center gap-2 rounded-full border border-rose-500/40 px-5 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-500/10 disabled:opacity-50"
          >
            <XCircle className="w-4 h-4" /> Reject
          </button>
          <button
            type="button"
            onClick={() => setShowCloseModal(true)}
            disabled={actionPending}
            className="inline-flex items-center gap-2 rounded-full border border-slate-500/40 px-5 py-2 text-sm font-semibold text-slate-400 hover:bg-slate-500/10 disabled:opacity-50"
          >
            Close request
          </button>
        </div>
      )}

      <ConfirmModal
        open={showApproveConfirm}
        title="Approve refund request"
        description="This re-validates the refund amount fresh against the current remaining refundable balance and immediately initiates the Razorpay refund. This cannot be undone."
        confirmLabel="Approve & refund"
        loading={actionPending}
        onClose={() => setShowApproveConfirm(false)}
        onConfirm={() => void onApprove()}
      >
        <label className="block text-[10px] uppercase tracking-widest font-bold text-on-surface-variant mb-1">
          Confirm the refund type (the customer chose: {row.category === 'PACK_REFUND' ? 'pack refund' : row.category === 'PAYMENT_ISSUE' ? 'payment issue' : 'service fault'})
        </label>
        <select
          value={confirmedCategory}
          onChange={(e) => setConfirmedCategory(e.target.value as 'SERVICE_FAULT' | 'PAYMENT_ISSUE' | 'PACK_REFUND')}
          className="w-full px-3 py-2 rounded-lg bg-surface-container-lowest border border-border-subtle text-sm"
        >
          <option value="SERVICE_FAULT">Service fault — I verified the invitation/template; needs a flagged fault</option>
          <option value="PAYMENT_ISSUE">Payment issue — I verified it in Razorpay</option>
          <option value="PACK_REFUND">Pack refund — unused credits (paid − credits used)</option>
        </select>
      </ConfirmModal>

      <ConfirmModal
        open={showRejectModal}
        title="Reject refund request"
        description="The user will be notified with the reason below."
        confirmLabel="Reject"
        destructive
        loading={actionPending}
        disableConfirm={decisionReason.trim().length < 3}
        onClose={() => setShowRejectModal(false)}
        onConfirm={() => void onReject()}
      >
        <textarea
          value={decisionReason}
          onChange={(e) => setDecisionReason(e.target.value)}
          rows={3}
          placeholder="Why is this request being rejected? (min 3 chars)"
          className="w-full px-3 py-2 rounded-lg bg-surface-container-lowest border border-border-subtle text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30"
        />
      </ConfirmModal>

      <ConfirmModal
        open={showCloseModal}
        title="Close refund request"
        description="Close this request without a customer-facing rejection (e.g. mistaken or duplicate submission). Any held credits will be returned to the partner."
        confirmLabel="Close request"
        destructive
        loading={actionPending}
        disableConfirm={closeReason.trim().length < 3}
        onClose={() => setShowCloseModal(false)}
        onConfirm={() => void onCloseRequest()}
      >
        <textarea
          value={closeReason}
          onChange={(e) => setCloseReason(e.target.value)}
          rows={3}
          placeholder="Internal reason for closing this request (min 3 chars)"
          className="w-full px-3 py-2 rounded-lg bg-surface-container-lowest border border-border-subtle text-sm focus:outline-none focus:ring-2 focus:ring-slate-500/30"
        />
      </ConfirmModal>
    </div>
  );
}

function Field({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-on-surface-variant mb-1">{label}</div>
      <div className="text-sm text-on-surface break-words">{value}</div>
      {hint && <div className="text-[10px] text-on-surface-variant mt-0.5">{hint}</div>}
    </div>
  );
}
